import React, { useState, useEffect } from 'react';
import SalesDetailPage from './SalesDetailPage';
import { EVENT_HEALTH_COLUMNS, SORT_TYPES, getDefaultVisibleColumns, getDefaultHiddenColumns } from '../constants/eventHealthColumnsConfig';
import { buildEventHealthFilterGroups } from '../constants/eventHealthFiltersConfig';

const axios = require('axios').default;

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.venue_name && activeFilters.venue_name.length > 0) {
    params.venue_name = activeFilters.venue_name;
  }
  if (activeFilters.event_name && activeFilters.event_name.length > 0) {
    params.event_name = activeFilters.event_name;
  }
  if (activeFilters.event_status && activeFilters.event_status.length > 0) {
    params.event_status = activeFilters.event_status;
  }
  if (activeFilters.presenter && activeFilters.presenter.length > 0) {
    params.presenter = activeFilters.presenter;
  }
  if (activeFilters.capacity) {
    if (activeFilters.capacity.min != null) params.capacity_min = activeFilters.capacity.min;
    if (activeFilters.capacity.max != null) params.capacity_max = activeFilters.capacity.max;
  }
  if (activeFilters.ticket_forecast) {
    if (activeFilters.ticket_forecast.min != null) params.ticket_forecast_min = activeFilters.ticket_forecast.min;
    if (activeFilters.ticket_forecast.max != null) params.ticket_forecast_max = activeFilters.ticket_forecast.max;
  }
  if (activeFilters.ticket_count) {
    if (activeFilters.ticket_count.min != null) params.ticket_count_min = activeFilters.ticket_count.min;
    if (activeFilters.ticket_count.max != null) params.ticket_count_max = activeFilters.ticket_count.max;
  }
  if (activeFilters.sold_today) {
    if (activeFilters.sold_today.min != null) params.sold_today_min = activeFilters.sold_today.min;
    if (activeFilters.sold_today.max != null) params.sold_today_max = activeFilters.sold_today.max;
  }
  if (activeFilters.recent_7day) {
    if (activeFilters.recent_7day.min != null) params.recent_7day_min = activeFilters.recent_7day.min;
    if (activeFilters.recent_7day.max != null) params.recent_7day_max = activeFilters.recent_7day.max;
  }
  if (activeFilters.tickets_gross) {
    if (activeFilters.tickets_gross.min != null) params.tickets_gross_min = activeFilters.tickets_gross.min;
    if (activeFilters.tickets_gross.max != null) params.tickets_gross_max = activeFilters.tickets_gross.max;
  }
  if (activeFilters.profit_loss) {
    if (activeFilters.profit_loss.min != null) params.profit_loss_min = activeFilters.profit_loss.min;
    if (activeFilters.profit_loss.max != null) params.profit_loss_max = activeFilters.profit_loss.max;
  }
  return params;
};

const EVENT_HEALTH_CONFIG = {
  pageTitle: 'Event Health',
  pageSubtitle: 'Ticket sales velocity and financial health across your events.',
  sidebarKey: 'marketing_event_health',
  defaultPreset: 'next_90_days',
  dateModeOptions: [
    { key: 'event_date', label: 'Event Date' },
  ],
  defaultSortRules: [{ field: 'event_date', direction: 'asc' }],

  columns: EVENT_HEALTH_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingMarketingEventHealth_v3_${teamId}`,
  firstColumnId: 'event_id',

  kpiFromDetailResponse: false,
  buildKpiCards: () => [],
  kpiGridColumns: 0,
  salesDataKey: null,
  venueSeriesKey: null,
  overTimeKey: null,
  chartColor: 'var(--sl-fg-brand, #1982c4)',
  topCharts: [],
  groupByOptions: null,
  groupByDefault: null,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/marketing/event-health/data.json`,
  detailResponseKey: 'events',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/marketing/event-health/export`,
  salesExportSection: 'event_health',
  detailErrorMessage: 'Failed to load event health data.',
  detailTableTitle: 'Event Health',
  emptyMessage: 'No events found.',
  searchPlaceholder: 'Search events...',
  paginationLabel: 'events',
  itemLabel: (count) => `event${count !== 1 ? 's' : ''}`,

  buildDetailApiFilters,

  renderCellValue: (col, event, { formatEventDate, formatEventDateTime, currencyFormatter: cf }) => {
    const forecast = event.ticket_forecast || 0;
    const pctToForecast = forecast > 0 ? ((event.ticket_count || 0) / forecast * 100) : 0;
    const expectedVsForecast = forecast > 0 ? ((event.expected_finish || 0) / forecast * 100) : 0;

    const pctColor = (pct) => {
      if (pct >= 100) return { background: '#2e7d32', color: '#fff' };
      if (pct >= 80) return { background: '#66bb6a', color: '#fff' };
      if (pct >= 60) return { background: '#fdd835', color: '#333' };
      if (pct > 0) return { background: '#ef5350', color: '#fff' };
      return { background: '#c62828', color: '#fff' };
    };

    const pctBadge = (pct) => {
      const colors = pctColor(pct);
      return (
        <span
          className="d-inline-block text-center font-weight-bold"
          style={{ ...colors, padding: '3px 10px', borderRadius: 4, fontSize: 12, minWidth: 60 }}
        >
          {pct > 0 ? `${pct.toFixed(pct % 1 === 0 ? 0 : 2)}%` : '0%'}
        </span>
      );
    };

    switch (col.id) {
      case 'event_id': return event.id || '\u2014';
      case 'event_date': return <span className="text-nowrap">{event.event_date ? formatEventDate(event.event_date) : '\u2014'}</span>;
      case 'event_name': return event.event_name || '\u2014';
      case 'event_status': return event.event_status ? event.event_status.charAt(0).toUpperCase() + event.event_status.slice(1) : '\u2014';
      case 'venue_name': return event.venue_name || '\u2014';
      case 'presenter': return event.presenter || '\u2014';
      case 'event_capacity': return (event.event_capacity || 0).toLocaleString();
      case 'ticket_count': return (event.ticket_count || 0).toLocaleString();
      case 'ticket_forecast': return (event.ticket_forecast || 0).toLocaleString();
      case 'pct_to_forecast': return forecast > 0 ? `${pctToForecast.toFixed(pctToForecast % 1 === 0 ? 0 : 2)}%` : '\u2014';
      case 'expected_finish': return (event.expected_finish || 0).toLocaleString();
      case 'expected_finish_vs_forecast': return forecast > 0 ? pctBadge(expectedVsForecast) : pctBadge(0);
      case 'tickets_gross': return cf.format(event.tickets_gross || 0);
      case 'tickets_sold_today': return (event.tickets_sold_today || 0).toLocaleString();
      case 'tickets_sold_yesterday': return (event.tickets_sold_yesterday || 0).toLocaleString();
      case 'tickets_sold_last_14_days': return (event.tickets_sold_last_14_days || 0).toLocaleString();
      case 'tickets_sold_last_30_days': return (event.tickets_sold_last_30_days || 0).toLocaleString();
      case 'tickets_sold_last_60_days': return (event.tickets_sold_last_60_days || 0).toLocaleString();
      case 'recent_7day': return (event.recent_7day || 0).toLocaleString();
      case 'profit_loss': return cf.format(event.profit_loss || 0);
      case 'profit_loss_estimate': return cf.format(event.profit_loss_estimate || 0);
      case 'profit_loss_forecast': return cf.format(event.profit_loss_forecast || 0);
      case 'days_on_sale': return event.days_on_sale != null ? event.days_on_sale.toLocaleString() : '\u2014';
      default: return '\u2014';
    }
  },

  filterTitle: 'Filter Events',
  filterSubtitle: 'Apply filters to narrow event health data.',
};

function ReportingMarketingEventHealthPage({ team, csrfToken, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    if (!team || !team.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.get(`/teams/${team.id}/reporting/marketing/event-health/filter_options.json`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch((err) => console.error('Failed to load filter options', err));
  }, [team?.id, csrfToken]);

  const configWithFilters = {
    ...EVENT_HEALTH_CONFIG,
    buildFilterGroups: () => buildEventHealthFilterGroups(filterOptions),
  };
  return <SalesDetailPage {...props} team={team} csrfToken={csrfToken} config={configWithFilters} />;
}

export default ReportingMarketingEventHealthPage;
